import React, { useCallback, useRef, useState } from 'react';
import { Box, VStack } from '@televet/kibble-ui/build/chakra';
import { AutomationPrompt } from '../../../types/AutomationPrompt';
import { PromptBlock } from './PromptBlock';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { AutomationsFieldLabel, AutomationsFieldDescription } from '../../Layout';
import { SelectPromptTypeBlock } from './SelectPromptTypeBlock';
import { PromptErrorDetail } from 'pages/Automations/types/AutomationValidation';
import { updateAutomationErrors } from 'pages/Automations/state/automationsSlice';

export const AutomationPrompts = (): JSX.Element => {
  const promptsList = useAppSelector((state) => state.automations.automationDraft.prompts);
  const promptErrors = useAppSelector((state) => state.automations.errors.prompts);
  const promptListRefs = useRef<HTMLDivElement[]>([]);
  const dispatch = useAppDispatch();
  const [scrollToPromptIndex, setScrollToPromptIndex] = useState<{ currentIndex: number; newIndex: number }>({
    currentIndex: -1,
    newIndex: -1,
  });

  const promptBlockCallbackRef = useCallback(() => {
    if (scrollToPromptIndex.newIndex < 0) return;

    // Scroll the user to the new prompt location
    if (promptListRefs?.current[scrollToPromptIndex.newIndex]) {
      const focusedItem = promptListRefs?.current[scrollToPromptIndex.newIndex];
      focusedItem.scrollIntoView({ behavior: 'smooth' });
    }

    // Update validation to match new order and clear 'nextStep' errors
    if (!!promptErrors.length) {
      const newPromptErrors: PromptErrorDetail[] = promptErrors.map((promptError): PromptErrorDetail => {
        const { buttons, promptIndex } = promptError;
        const promptBeingMoved = promptIndex === scrollToPromptIndex.currentIndex;
        const promptBeingReplaced = promptIndex === scrollToPromptIndex.newIndex;

        if (promptBeingMoved || promptBeingReplaced) {
          return {
            ...promptError,
            nextStep: [],
            promptIndex: promptBeingMoved ? scrollToPromptIndex.newIndex : scrollToPromptIndex.currentIndex,
            buttons: buttons ? buttons.map((button) => ({ ...button, buttonNextStep: [] })) : [],
          };
        }

        return promptError;
      });

      dispatch(updateAutomationErrors({ prompts: newPromptErrors }));
    }

    // Reset prompt scroll to index
    setScrollToPromptIndex({
      currentIndex: -1,
      newIndex: -1,
    });
  }, [dispatch, scrollToPromptIndex, promptErrors]);

  return (
    <Box className="Automations__PromptsContainer">
      <AutomationsFieldLabel>Automation Prompts</AutomationsFieldLabel>
      <AutomationsFieldDescription>
        Prompts are the bread and butter of automations; use them to deliver appointment information and collect
        information from clients.
      </AutomationsFieldDescription>

      {!!promptsList.length ? (
        <VStack pt={4} spacing={8} className="Automations__PromptList">
          {promptsList.map((prompt: AutomationPrompt, index: number) => {
            return (
              <PromptBlock
                errors={promptErrors.find((promptError) => promptError.promptIndex === index)}
                key={index}
                index={index}
                isFirstPrompt={index === 0}
                isLastPrompt={index === promptsList.length - 1}
                prompt={prompt}
                setScrollToPromptIndex={setScrollToPromptIndex}
                promptListRefs={promptListRefs}
                promptBlockCallbackRef={promptBlockCallbackRef}
              />
            );
          })}
        </VStack>
      ) : (
        <SelectPromptTypeBlock closeButtonVisible={false} addPromptAtIndex={0} />
      )}
    </Box>
  );
};
